import { GetProject } from '@/connection/projects/get-project';
import { RootState } from '@/store/main';
import { BrandliveEvent } from '@/types/projects.types';
import { Session } from '@/types/session.types';
import { CREATE_FROM_TEMPLATE_IDB_KEY } from '@/utils/create-and-edit-project-helpers/create-from-template.types';
import db from '@/utils/indexed-db';

interface Props {
  projectUuid?: string;
  withCache?: boolean;
  resetValues?: boolean;
  project: BrandliveEvent | null;
  getState: () => unknown;
  rejectWithValue: (value: unknown) => unknown;
}

const loadWorkingProjectWithIndexedDBReducerHelper = async ({
  project,
  projectUuid,
  withCache,
  resetValues,
  getState,
  rejectWithValue,
}: Props) => {
  let _project = project;
  const storedProject = await db.get<BrandliveEvent>(CREATE_FROM_TEMPLATE_IDB_KEY, CREATE_FROM_TEMPLATE_IDB_KEY);
  if (withCache) {
    if (storedProject) {
      _project = storedProject.data;
    }
  }

  if (!_project && projectUuid) {
    const result = await GetProject(projectUuid);
    if ('error' in result) {
      console.error('loadProject error', result.error);
      throw rejectWithValue(result.error);
    }
    _project = result;
  }

  if (!_project) return null;

  const store = getState() as RootState;
  const workingProject = store.project.workingProject as BrandliveEvent | null;
  if (workingProject && workingProject.uuid === project?.uuid) {
    return null;
  }

  let resetProject = _project;
  if (resetValues) {
    const filteredSessions = _project.sessions as Session[];
    resetProject = {
      ..._project,
      // set fields that require updating to their "empty" states
      name: '',
      sessions: filteredSessions.map((s) => {
        if (!s) return s;
        // set every field in title to an empty string
        const title = { ...s.title };
        Object.keys(s.title).forEach((key) => {
          title[key] = '';
        });
        return {
          ...s,
          title,
          // initialize session with null timestamps
          // this forces the user to set the timestamps before saving the project
          timestamp: null,
          end_timestamp: null,
        };
      }),
    };
  }
  // if the project is not stored in indexedDB, store it
  if (withCache) {
    if (!storedProject?.data) {
      await db.put({
        uuid: CREATE_FROM_TEMPLATE_IDB_KEY,
        type: CREATE_FROM_TEMPLATE_IDB_KEY,
        data: resetProject,
      });
      return resetProject;
    } else if (_project.uuid && storedProject.data.uuid !== _project.uuid) {
      // if the project is stored in indexedDB, but it is not the project we are trying to load, then replace it
      await db.put({
        uuid: CREATE_FROM_TEMPLATE_IDB_KEY,
        type: CREATE_FROM_TEMPLATE_IDB_KEY,
        data: resetProject,
      });
      return resetProject;
    }
  }

  return resetProject;
};

export default loadWorkingProjectWithIndexedDBReducerHelper;
